import { Component, ElementRef, OnInit, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { StrategyService } from '../../services/strategy.service';
import { DropdownModule } from 'primeng/dropdown';
import { SocketService } from '../../services/socket.service';
import { DirectionService } from '../../services/direction.service';
import { sidebarTogglerService } from '../../services/sidebar-toggler.service';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { OrgChart } from 'd3-org-chart';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';


import { NavTitlePipe } from '../../pipes/nav-title.pipe';

@Component({
  selector: 'app-organization-hierarchy',
  standalone: true,
  imports: [
    NavTitlePipe,DropdownModule, ButtonModule, TranslateModule, CommonModule],
  templateUrl: './organization-hierarchy.component.html',
  styleUrl: './organization-hierarchy.component.scss'
})
export class OrganizationHierarchyComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('chartContainer') chartContainer!: ElementRef;

  hierarchy = {} as any
  chart = {} as any

  sidebarShown = false;
  dir!: 'rtl' | 'ltr';

  constructor(
    private strategyService: StrategyService,
    private directionService: DirectionService,
    private sidebarTogglerService: sidebarTogglerService,
    private router: Router
  ) {


  }
  ngOnInit(): void {
    this.directionService.direction$.subscribe((dir) => {
      this.dir = dir;
    })
    this.sidebarTogglerService.setSidaBarState(false);
    this.strategyService.GetOrganizationHierarchy().subscribe({
      next: (response: any) => {
        console.log('🔍 Organization Hierarchy API Response:', response);
        
        // Transform departments structure to flat units structure
        const departments = response['data']['hierarchy']['departments'] || [];
        this.hierarchy = this.transformDepartmentsToFlatUnits(departments);
        
        console.log('📊 Transformed Hierarchy Data:', this.hierarchy);
        console.log('📏 Hierarchy Length:', this.hierarchy?.length || 0);
        
        // Create chart after data is loaded and view is ready
        if (this.hierarchy && this.hierarchy.length > 0) {
          setTimeout(() => {
            this.createChart();
          }, 1000);
        } else {
          console.warn('⚠️ No hierarchy data received or empty array');
        }
      },
      error: (error) => {
        console.error('❌ Error loading organization hierarchy:', error);
      }
    })
  }

  private transformDepartmentsToFlatUnits(departments: any[]): any[] {
    const units: any[] = [];
    
    // Add root organization node
    units.push({
      id: 'org',
      name: 'مطار الشارقة',
      parentId: null,
      type: 'organization',
      no_departments: departments.length,
      no_sections: departments.reduce((sum, d) => sum + d.no_sections, 0),
      no_offices: 0
    });
    
    // Add each department and its sections
    departments.forEach((dept: any) => {
      // Add department
      units.push({
        id: `dept_${dept.id}`,
        name: dept.name,
        parentId: 'org',
        type: 'department',
        no_sections: dept.no_sections || dept.sections?.length || 0
      });
      
      // Add sections under this department
      if (dept.sections && dept.sections.length > 0) {
        dept.sections.forEach((section: any) => {
          units.push({
            id: `section_${section.id}`,
            name: section.name,
            parentId: `dept_${dept.id}`,
            type: 'section'
          });
        });
      }
    });
    
    return units;
  }

  ngAfterViewInit(): void {
    // View is now initialized, ensure chart container is ready
    if (this.hierarchy && Object.keys(this.hierarchy).length > 0) {
      setTimeout(() => {
        this.createChart();
      }, 500);
    }
  }

  ngOnDestroy(): void {
    this.sidebarTogglerService.setSidaBarState(true);
    // Clean up chart instance
    if (this.chart && this.chart.render) {
      try {
        this.chart.render().clear();
      } catch (e) {
        console.warn('Error clearing chart on destroy:', e);
      }
    }
  }

  goBack() {
    history.back();
  }

  private createChart(): void {
    const data = this.hierarchy;
    console.log('📊 createChart() called with data:', data);
    console.log('📏 Data type:', typeof data);
    console.log('📋 Is Array:', Array.isArray(data));
    console.log('🔢 Data length:', data?.length);

    // Validate data
    if (!data || !Array.isArray(data) || data.length === 0) {
      console.error('❌ Invalid or empty data for org chart:', data);
      return;
    }

    // Check if container exists and has dimensions before creating chart
    const container = document.querySelector('#chartContainer') as HTMLElement;
    if (!container) {
      console.warn('⚠️ Chart container not found, retrying in 200ms...');
      setTimeout(() => {
        this.createChart();
      }, 200);
      return;
    }

    // Check if container has dimensions
    const rect = container.getBoundingClientRect();
    console.log('📐 Container dimensions:', rect);
    if (rect.width === 0 || rect.height === 0) {
      console.warn('⚠️ Chart container has no dimensions, retrying in 200ms...');
      setTimeout(() => {
        this.createChart();
      }, 200);
      return;
    }

    // Clear any existing chart
    if (this.chart && this.chart.render) {
      try {
        this.chart.render().clear();
      } catch (e) {
        console.warn('Error clearing existing chart:', e);
      }
    }

    try {
      console.log('🎨 Creating OrgChart with d3-org-chart library...');
      this.chart = new OrgChart()
        .container('#chartContainer')
        .data(data)
      .nodeWidth((d: any) => 200)
      .nodeHeight((d: any) => 110)
      .childrenMargin((d: any) => 150)
      .compactMarginBetween((d: any) => 80)
      .compactMarginPair((d: any) => 150)
      .siblingsMargin((d: any) => 80)
      .neighbourMargin((d: any) => 100)
      .nodeContent((d: any) => {
        const childLayerBg = '#6b7280';
        const childLayerColor = '#ffffff';
        let selectedText = '';
        if (d['data']['type'] == 'organization') {
          selectedText = `
          <div id='1' style="font-size: 0.9rem;font-family: 'Cairo', sans-serif; color:white"
          class="g-bg-primary-darker gap-1 flex flex-column h-5rem p-2 border-round-lg font-bold ">
          <div class="text-center">${d['data'].name}</div>
          <div class="flex justify-content-evenly cursor-pointer">
              <div>
                  <span class="g-c-white font-bold">
                      ${d['data'].no_departments}
                  </span>
                  <span class="g-c-white">
                      <i class="fa-solid fa-sitemap"></i>
                  </span>
              </div>
              <div>
                  <span class="g-c-white font-bold">
                      ${d['data'].no_sections}
                  </span>
                  <span class="g-c-white">
                      <i class="fa-solid fa-folder-tree"></i>
                  </span>
              </div>
              <div>
                  <span class="g-c-white font-bold">
                      ${d['data'].no_offices}
                  </span>
                  <span class="g-c-white">
                      <i class="fa-solid fa-building-user"></i>
                  </span>
              </div>
          </div>
      </div>
      `
        } else if (d['data']['type'] == 'department_head') {
          selectedText = `
          <div id='1' style="font-size: 0.9rem;font-family: 'Cairo', sans-serif; color:white"
          class="g-bg-primary-darker gap-1 flex flex-column h-5rem p-2 border-round-lg font-bold ">
          <div class="text-center">${d['data'].name}</div>
          <div class="flex justify-content-evenly cursor-pointer">
              <div>
                  <span class="g-c-white font-bold">
                      ${d['data'].no_departments}
                  </span>
                  <span class="g-c-white">
                      <i class="fa-solid fa-sitemap"></i>
                  </span>
              </div>
              <div>
                  <span class="g-c-white font-bold">
                      ${d['data'].no_sections}
                  </span>
                  <span class="g-c-white">
                      <i class="fa-solid fa-folder-tree"></i>
                  </span>
              </div>
              <div>
                  <span class="g-c-white font-bold">
                      ${d['data'].no_offices}
                  </span>
                  <span class="g-c-white">
                      <i class="fa-solid fa-building-user"></i>
                  </span>
              </div>
          </div>
      </div>
      `
        }
        else if (d['data']['type'] == 'office') {
          selectedText = `
          <div id='1' style="font-size: 0.9rem;font-family: 'Cairo', sans-serif; background-color:${childLayerBg}; color:${childLayerColor}"
          class="gap-1 flex flex-column h-5rem p-2 border-round-lg font-bold ">
          <div class="text-center">${d['data'].name}</div>
      </div>
      `
        } else if (d['data']['type'] == 'department') {
          selectedText = `
        <div style="font-size: 0.9rem;font-family: 'Cairo', sans-serif; background-color:${childLayerBg}; color:${childLayerColor}" class="flex flex-column h-5rem p-2 border-round-lg font-bold ">
                    <div class="text-center">${d['data'].name}</div>
                    <div class="flex justify-content-evenly cursor-pointer">
                           <div>
                  <span class="g-c-white font-bold">
                          ${d['data'].no_sections}
                        </span>
                        <span class="g-c-white">
                         <i class="fa-solid fa-folder-tree"></i>
                        </span>
                   </div>
                    </div>
        </div>
      `
        }
        else {
          selectedText = `
            <div style="font-size: 0.9rem;font-family: 'Cairo', sans-serif; background-color:${childLayerBg}; color:${childLayerColor}" class="gap-3 flex flex-column h-4rem p-2 border-round-lg font-bold ">
                 <div class="text-center">${d['data'].name}</div>     
            </div>
      `
        }
        return selectedText;
      }
      )
      .render();
      
      console.log('✅ OrgChart rendered successfully!');
      console.log('📊 Chart instance:', this.chart);
    } catch (error) {
      console.error('❌ Error creating organization chart:', error);
      console.error('❌ Error details:', JSON.stringify(error, null, 2));
      // Retry after a delay if there's an error
      setTimeout(() => {
        this.createChart();
      }, 1000);
    }
  }
  expandAll(): void {
    if (this.chart && this.chart.expandAll && typeof this.chart.expandAll === 'function') {
      try {
        this.chart.expandAll();
      } catch (error) {
        console.error('Error expanding all nodes:', error);
      }
    }
  }

  collapseAll(): void {
    if (this.chart && this.chart.collapseAll && typeof this.chart.collapseAll === 'function') {
      try {
        this.chart.collapseAll();
      } catch (error) {
        console.error('Error collapsing all nodes:', error);
      }
    }
  }
  
  compact(status: boolean): void {
    if (this.chart && this.chart.compact && typeof this.chart.compact === 'function') {
      try {
        this.chart.compact(status).render().fit();
      } catch (error) {
        console.error('Error compacting chart:', error);
      }
    }
  }

  zoomIn() {
    if (this.chart && this.chart.zoomIn && typeof this.chart.zoomIn === 'function') {
      try {
        this.chart.zoomIn();
      } catch (error) {
        console.error('Error zooming in:', error);
      }
    }
  }

  zoomOut() {
    if (this.chart && this.chart.zoomOut && typeof this.chart.zoomOut === 'function') {
      try {
        this.chart.zoomOut();
      } catch (error) {
        console.error('Error zooming out:', error);
      }
    }
  }

  centerToroot() {
    if (this.chart && this.chart.setscreenSize && typeof this.chart.setscreenSize === 'function') {
      try {
        this.chart.setscreenSize(this.chart.width, this.chart.height);
      } catch (error) {
        console.error('Error centering to root:', error);
      }
    }
  }

  fitScreen() {
    if (this.chart && this.chart.fit && typeof this.chart.fit === 'function') {
      try {
        this.chart.fit();
      } catch (error) {
        console.error('Error fitting screen:', error);
      }
    }
  }

  exportCurrentImg() {
    // this.chart.exportImg();
    window.print();
  }

  exportFullImg() {
    this.chart.exportImg({
      full: true,
      scale: 2
    });
  }

  printPage() {
    window.print();
  }
  printOrgChart(): void {
    const svgEl = document.querySelector('.svg-chart-container') as SVGSVGElement;
    console.log("svgEl", svgEl)

    if (!svgEl) {
      console.error('SVG not found');
      return;
    }

    // Clone the SVG
    const clonedSvg = svgEl.cloneNode(true) as SVGSVGElement;

    // ✅ Force explicit size for print (d3-org-chart uses viewBox + transforms)
    const bbox = svgEl.getBBox();
    clonedSvg.setAttribute('width', `${bbox.width + 100}`);
    clonedSvg.setAttribute('height', `${bbox.height + 100}`);
    clonedSvg.setAttribute('viewBox', `0 0 ${bbox.width + 100} ${bbox.height + 100}`);

    // Wrap it for printing
    const wrapper = document.createElement('div');
    wrapper.id = 'print-orgchart-wrapper';
    wrapper.style.cssText = `
      width: 100%;
      height: 100%;
      padding: 2rem;
      background: white;
      display: flex;
      justify-content: center;
      align-items: center;
    `;
    wrapper.appendChild(clonedSvg);

    // Style block for print colors
    const style = document.createElement('style');
    style.textContent = `
      @media print {
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        body * {
          visibility: hidden !important;
        }
        #print-orgchart-wrapper, #print-orgchart-wrapper * {
          visibility: visible !important;
        }
        #print-orgchart-wrapper {
          position: absolute;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background-color: white;
          z-index: 9999;
        }
        svg {
          display: block;
          margin: auto;
        }
      }
    `;

    document.body.appendChild(style);
    document.body.appendChild(wrapper);

    setTimeout(() => {
      window.print();

      setTimeout(() => {
        wrapper.remove();
        style.remove();
      }, 500);
    }, 500); // Delay to allow full render
  }

  /**
   * Export the org chart as a PDF file
   */
  exportOrgChartAsPDF(): void {
    const chartElement = document.querySelector('.svg-chart-container') as HTMLElement;
    if (!chartElement) {
      console.error('SVG chart container not found');
      return;
    }
    // Wrap SVG in a div for html2canvas
    const wrapper = document.createElement('div');
    wrapper.style.background = 'white';
    wrapper.appendChild(chartElement.cloneNode(true));
    document.body.appendChild(wrapper);

    html2canvas(wrapper, {
      backgroundColor: '#fff',
      useCORS: true,
      scale: 2
    }).then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      // Calculate width/height to fit A4
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      let ratio = Math.min(pageWidth / imgWidth, pageHeight / imgHeight);
      let pdfWidth = imgWidth * ratio;
      let pdfHeight = imgHeight * ratio;
      pdf.addImage(imgData, 'PNG', (pageWidth - pdfWidth) / 2, 20, pdfWidth, pdfHeight);
      pdf.save('organization-hierarchy.pdf');
      wrapper.remove();
    }).catch((err) => {
      console.error('Error exporting org chart as PDF:', err);
      wrapper.remove();
    });
  }


}